import { useEffect } from 'react';

export const useScrollAnimations = () => {
  useEffect(() => {
    // Position video to match sensor image
    const positionVideo = () => {
      const sensorImage = document.querySelector('.sensor-image') as HTMLElement;
      const scrollVideo = document.querySelector('.scroll-video') as HTMLVideoElement;
      
      if (sensorImage && scrollVideo) {
        const rect = sensorImage.getBoundingClientRect();
        scrollVideo.style.position = 'fixed';
        scrollVideo.style.left = `${rect.left}px`;
        scrollVideo.style.top = `${rect.top}px`;
        scrollVideo.style.width = `${rect.width}px`;
        scrollVideo.style.height = `${rect.height}px`;
        scrollVideo.style.borderRadius = '12px';
        scrollVideo.style.overflow = 'hidden';
        // Add webkit prefix for better compatibility
        (scrollVideo.style as any).webkitBorderRadius = '12px';
        // Use clip-path as the primary method for desktop
        if (window.innerWidth >= 950) {
          // Use a precise clip-path with percentage-based radius for consistency
          const radiusPercent = (12 / rect.width) * 100; // Convert 12px to percentage
          scrollVideo.style.clipPath = `inset(0% round ${radiusPercent}%)`;
          (scrollVideo.style as any).webkitClipPath = `inset(0% round ${radiusPercent}%)`;
        }
        // Force GPU acceleration
        scrollVideo.style.transform = 'translateZ(0)';
        (scrollVideo.style as any).webkitTransform = 'translateZ(0)';
      }
    };
    
    // Initial positioning
    positionVideo();
    window.addEventListener('resize', positionVideo);
    
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.05,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          // Debug logging for dashboard
          if (entry.target.classList.contains('dashboard-img')) {
            console.log('Dashboard image should be visible now');
          }
        }
      });
    }, observerOptions);

    // Observe all elements with scroll animation classes
    const elements = document.querySelectorAll('.fade-up, .fade-in, .slide-in-left, .slide-in-right, .scale-in, .slide-up');
    elements.forEach(el => observer.observe(el));

    // Parallax effect for hero elements
    let hasScrolled = false;
    
    const handleScroll = () => {
      // Disable scroll animations on mobile
      if (window.innerWidth < 950) {
        return;
      }
      
      const scrolled = window.scrollY;
      const heroText = document.querySelector('.hero-text') as HTMLElement;
      const sensorContainer = document.querySelector('.sensor-container') as HTMLElement;
      const heroSection = document.querySelector('.hero-section') as HTMLElement;
      const techSection = document.querySelector('.technology-section') as HTMLElement;
      const scrollVideo = document.querySelector('.scroll-video') as HTMLVideoElement;
      
      // Position video to match sensor on scroll
      // Only update position if we're not in pitch deck or if we're before tech section
      const isPitchDeck = document.querySelector('.pitch-deck');
      const useCasesSection = document.querySelector('.use-cases-section');
      
      // Check if we should stop positioning the video
      let shouldPositionVideo = true;
      let videoReachedUseCases = false;
      
      if (isPitchDeck && useCasesSection) {
        const useCasesRect = useCasesSection.getBoundingClientRect();
        if (useCasesRect.top <= window.innerHeight * 0.8) {
          shouldPositionVideo = false;
          videoReachedUseCases = true;
        }
      }
      
      if (shouldPositionVideo && (!isPitchDeck || !techSection || scrolled < techSection.offsetTop - window.innerHeight / 2)) {
        positionVideo();
      }
      
      // Handle video fade-in and sensor fade-out
      const sensorImage = document.querySelector('.sensor-image') as HTMLElement;
      
      if (scrollVideo && techSection) {
        if (scrolled > 0) {
          // Calculate progress from start of scroll to tech section in view
          const techOffset = techSection.offsetTop;
          const progress = Math.max(0, Math.min(1, scrolled / (techOffset * 0.8)));
          
          // Fade in video based on scroll progress
          scrollVideo.style.opacity = progress.toString();
          
          // For pitch deck, hide video after technology section
          if (isPitchDeck) {
            if (videoReachedUseCases) {
              scrollVideo.style.opacity = '0';
              // Also stop the video when it's hidden
              scrollVideo.pause();
            }
          } else {
            // Keep video visible while scrolling through tech section
            const featureSection = document.querySelector('.features-section');
            if (featureSection) {
              const featureRect = featureSection.getBoundingClientRect();
              
              // Only fade out when features section is approaching
              if (featureRect.top < window.innerHeight) {
                const fadeDistance = 200; // Start fading 200px before features
                const fadeProgress = Math.max(0, Math.min(1, (window.innerHeight - featureRect.top) / fadeDistance));
                scrollVideo.style.opacity = (progress * (1 - fadeProgress)).toString();
              }
            }
          }
          
          // Fade out sensor image
          if (sensorImage) {
            sensorImage.style.opacity = (1 - progress).toString();
          }
          
          // Start playing video when scroll begins
          if (scrolled > 10 && scrollVideo.paused) {
            scrollVideo.play().catch(() => {});
          }
          
          // Don't sync video timeline with scroll - let it play naturally
        } else {
          // Reset when at top
          scrollVideo.style.opacity = '0';
          scrollVideo.pause();
          scrollVideo.currentTime = 0;
          
          // Reset sensor image opacity
          if (sensorImage) {
            sensorImage.style.opacity = '1';
          }
        }
      }
      
      // Mark that user has scrolled
      if (scrolled > 0) {
        hasScrolled = true;
      }
      
      // Only apply transforms after first scroll (but not to sensor container to keep video aligned)
      if (hasScrolled) {
        if (heroText && scrolled < window.innerHeight) {
          heroText.style.transform = `translateY(${scrolled * 0.5}px)`;
          heroText.style.opacity = `${1 - scrolled / window.innerHeight}`;
        }
        
        // Don't transform sensorContainer to keep video aligned with sensor
        if (sensorContainer && scrolled < window.innerHeight) {
          // Only fade the container, don't move it
          sensorContainer.style.opacity = `${1 - scrolled / window.innerHeight}`;
        }

        // Only fade hero on desktop, not mobile
        if (heroSection && scrolled < window.innerHeight && window.innerWidth >= 950) {
          heroSection.style.opacity = `${1 - scrolled / (window.innerHeight * 1.5)}`;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', positionVideo);
    };
  }, []);
};